import React from "react";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { formatMoney } from "@/lib/format";

const FilterGroup = ({ values, selected, onToggle, id }) => (
  <div className="space-y-1.5">
    {values.map((v) => {
      const isChecked = selected.includes(v);
      return (
        <label key={v} className="flex items-center gap-2 cursor-pointer text-sm hover:text-foreground text-muted-foreground py-0.5">
          <Checkbox
            checked={isChecked}
            onCheckedChange={() => onToggle(v)}
            data-testid={`filter-${id}-${v.toLowerCase().replace(/\s+/g, "-")}`}
          />
          <span>{v}</span>
        </label>
      );
    })}
  </div>
);

const SCHEMES = [
  { value: "eis", label: "EIS Eligible" },
  { value: "seis", label: "SEIS Eligible" },
  { value: "both", label: "EIS + SEIS" },
  { value: "none", label: "Neither" },
];

export default function FilterSidebar({ options, filters, setFilters, onReset }) {
  const toggleArr = (key, v) => {
    setFilters((f) => {
      const arr = f[key] || [];
      const next = arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
      return { ...f, [key]: next };
    });
  };

  const activeCount =
    (filters.industry?.length || 0) +
    (filters.stage?.length || 0) +
    (filters.revenue_status?.length || 0) +
    (filters.region?.length || 0) +
    (filters.country?.length || 0) +
    (filters.priority?.length || 0) +
    (filters.scheme ? 1 : 0) +
    (filters.min_raise > 50000 || filters.max_raise < 20000000 ? 1 : 0);

  return (
    <aside className="w-full lg:w-[260px] shrink-0">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Filters</div>
        {activeCount > 0 && (
          <Button variant="ghost" size="sm" onClick={onReset} data-testid="filter-reset-btn" className="h-6 text-xs">
            Clear ({activeCount})
          </Button>
        )}
      </div>

      <Accordion type="multiple" defaultValue={["region"]} className="w-full">
        <AccordionItem value="region" className="border-b border-border">
          <AccordionTrigger className="text-sm font-medium py-3 hover:no-underline">Region</AccordionTrigger>
          <AccordionContent className="pb-4">
            <FilterGroup id="region" values={options.regions || []} selected={filters.region || []} onToggle={(v) => toggleArr("region", v)} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="industry" className="border-b border-border">
          <AccordionTrigger className="text-sm font-medium py-3 hover:no-underline">Industry</AccordionTrigger>
          <AccordionContent className="pb-4">
            <FilterGroup id="industry" values={options.industries || []} selected={filters.industry || []} onToggle={(v) => toggleArr("industry", v)} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="stage" className="border-b border-border">
          <AccordionTrigger className="text-sm font-medium py-3 hover:no-underline">Stage</AccordionTrigger>
          <AccordionContent className="pb-4">
            <FilterGroup id="stage" values={options.stages || []} selected={filters.stage || []} onToggle={(v) => toggleArr("stage", v)} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="revenue" className="border-b border-border">
          <AccordionTrigger className="text-sm font-medium py-3 hover:no-underline">Revenue Status</AccordionTrigger>
          <AccordionContent className="pb-4">
            <FilterGroup id="revenue" values={options.revenue_statuses || []} selected={filters.revenue_status || []} onToggle={(v) => toggleArr("revenue_status", v)} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="raise" className="border-b border-border">
          <AccordionTrigger className="text-sm font-medium py-3 hover:no-underline">Ticket Size</AccordionTrigger>
          <AccordionContent className="pb-4 space-y-3">
            <div className="flex justify-between text-xs text-muted-foreground num-display">
              <span>{formatMoney(filters.min_raise)}</span>
              <span>{formatMoney(filters.max_raise)}</span>
            </div>
            <Slider
              min={50000}
              max={20000000}
              step={50000}
              value={[filters.min_raise, filters.max_raise]}
              onValueChange={([mn, mx]) => setFilters((f) => ({ ...f, min_raise: mn, max_raise: mx }))}
              data-testid="filter-raise-slider"
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="scheme" className="border-b border-border">
          <AccordionTrigger className="text-sm font-medium py-3 hover:no-underline">Tax Scheme</AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="space-y-1.5">
              {SCHEMES.map((s) => (
                <label key={s.value} className="flex items-center gap-2 cursor-pointer text-sm text-muted-foreground py-0.5">
                  <input
                    type="radio"
                    name="scheme"
                    checked={filters.scheme === s.value}
                    onChange={() => setFilters((f) => ({ ...f, scheme: f.scheme === s.value ? null : s.value }))}
                    data-testid={`filter-scheme-${s.value}`}
                    className="accent-emerald-600"
                  />
                  <span>{s.label}</span>
                </label>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="priority" className="border-b border-border">
          <AccordionTrigger className="text-sm font-medium py-3 hover:no-underline">Priority</AccordionTrigger>
          <AccordionContent className="pb-4">
            <FilterGroup id="priority" values={options.priorities || []} selected={filters.priority || []} onToggle={(v) => toggleArr("priority", v)} />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </aside>
  );
}
